import useOnWindowFocus from '@/hooks/useWindowFocus';
import { useState, useEffect } from 'react';
import { LIMIT_OF_SELECTED_ITEMS } from './types';
import { cookieStorage } from '@/services/cookie';
import config from 'config-base';
import { getProductType, getQueryData, extractFirstSegment } from './getQueryData';

const useCompare = (initialData: Array<string>) => {
  const [compareData, setCompareData] = useState<Array<string>>(initialData);
  const [showAlertPopup, setShowAlertPopup] = useState(false);
  const { isWindowFocused } = useOnWindowFocus();

  const calcItemsIds = () => {
    const urls = compareData ? [...new Set(compareData.map(extractFirstSegment))].join('/') : '';
    // Determine the format type based on the first item
    const firstItem = compareData && compareData.length > 0 ? compareData[0] : '';
    const parts = firstItem.split('-');
    let afterId = '';
    let isNumber = false;
    if (parts.length >= 2) {
      // Check if second part is a single letter (A, P, etc.)
      if (parts[1] && parts[1].match(/^[A-Z]$/)) {
        afterId = parts[1]; // Just the letter, e.g., "A" or "P"
        isNumber = false;
      } else {
        // It's a numeric format
        afterId = parts[1]; // The number part, e.g., "5775"
        isNumber = true; // This should use G/ format
      }
    }
    return { urls, afterId, isNumber };
  };

  const handleRedirectCompare = () => {
    const { urls, afterId, isNumber } = calcItemsIds();
    if (isNumber) {
      window.location.href = `${config.mainPyramydairUrl}/compare/G/${urls}`;
    } else {
      window.location.href = `${config.mainPyramydairUrl}/compare/${afterId[0]}/${urls}`;
    }
  };

  const setCompareCookie = async () => {
    try {
      const { urls, afterId, isNumber } = calcItemsIds();

      if (!urls) {
        await cookieStorage.remove({ key: 'compareList' });
        return;
      }
      if (isNumber) {
        await cookieStorage.put({
          key: 'compareList',
          value: `G/${urls}/`,
          options: {
            expires: 1,
            sameSite: 'strict',
            path: '/',
          },
        });
      } else {
        await cookieStorage.put({
          key: 'compareList',
          value: `${afterId[0]}/${urls}/`,
          options: {
            expires: 1,
            sameSite: 'strict',
            path: '/',
          },
        });
      }
    } catch (error) {
      console.log('cant handle cookie in compare search results', error);
    }
  };

  const handleCompare = (itemId: string, val: boolean) => {
    if (!val) {
      const itemNumber = extractFirstSegment(itemId);
      setCompareData(compareData?.filter((id: string) => extractFirstSegment(id) !== itemNumber) || []);
      return;
    }

    const inputProductType = getProductType(itemId);
    const equalProductType = compareData?.every((id) => getProductType(id) === inputProductType);

    // Also check if item is already selected by comparing ItemNumbers
    const itemNumber = extractFirstSegment(itemId);
    const isAlreadySelected = compareData?.some((id) => extractFirstSegment(id) === itemNumber);

    if (!equalProductType) {
      setCompareData([itemId]);
      return;
    }

    if (isAlreadySelected) {
      // Item is already selected, don't add duplicate
      return;
    }

    if (compareData) {
      if (compareData.length === LIMIT_OF_SELECTED_ITEMS) {
        setShowAlertPopup(true);
        return;
      }
      setCompareData([...compareData, itemId]);
    } else {
      setCompareData([itemId]);
    }
  };

  const isSelectedToCompare = (compareData: Array<string> | undefined, itemId: string): boolean => {
    if (!compareData) {
      return false;
    }

    const itemNumber = extractFirstSegment(itemId);
    const isSelectedIndex = compareData.findIndex((id) => extractFirstSegment(id) === itemNumber);
    return isSelectedIndex > -1 && isSelectedIndex < LIMIT_OF_SELECTED_ITEMS;
  };

  const setCompareState = async () => {
    const compareList = (await cookieStorage.get('compareList')).data || '';
    if (!compareList) {
      setCompareData([]);
      return;
    }

    // Parse compareList using getQueryData to get proper PY- prefixed format
    const parsedData = getQueryData(compareList);
    setCompareData(parsedData);
  };

  useEffect(() => {
    if (isWindowFocused) {
      setCompareState();
    }
  }, [isWindowFocused]);

  useEffect(() => {
    if (compareData && compareData.length <= LIMIT_OF_SELECTED_ITEMS) {
      setCompareCookie();
    }
  }, [compareData]);

  return {
    compareData,
    showAlertPopup,
    isSelectedToCompare,
    handleCompare,
    handleRedirectCompare,
    handleCloseAlertPopup: () => setShowAlertPopup(false),
  };
};

export default useCompare;
